import React, { useState, useMemo, useCallback, useEffect } from 'react';
import '../../assets/css/case-city.css';
import { CityFilterBar, type FilterState } from './CityFilterBar';
import { CityPropertyCard } from './CityPropertyCard';
import { CityGoogleMap, type MapProperty, type MapBounds } from './CityGoogleMap';
import { CityPopularSearches } from './CityPopularSearches';
import { CityMobileTabs } from './CityMobileTabs';
import { propertySummaries, fetchPropertyDetail } from './propertyUtils';
import { getPropertyAddressUrl } from '@vibesites/runtime';

const GOOGLE_MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY) ||
  'AIzaSyClSFK0-GF1KdF6DEkYX_drD8qq2O20jPw';

// Per-property detail cache shared across all market page instances
const detailCache = new Map<string, any>();

interface MarketPageProps {
  /** Title text after the count, e.g. "Apartments in Tulsa, OK" */
  title: string;
  /** Initial map viewport. If omitted, fits to all properties. */
  initialBounds?: MapBounds;
  /** Scoped property list for this market. Falls back to all propertySummaries when omitted. */
  properties?: any[];
  /** Optional content (e.g. SEO block) rendered below the map */
  children?: React.ReactNode;
}

// ~0.045 degrees ≈ 3 miles — prevents map from zooming into street level
// for single-property or tightly clustered markets like Pryor or Stillwater.
const MIN_BOUNDS_SPAN = 0.045;

/** Compute a bounding box from an array of properties with lat/lng. */
export function computeBounds(properties: any[]): MapBounds | undefined {
  const coords = properties
    .filter((p: any) => p.property_address_latitude && p.property_address_longitude)
    .map((p: any) => ({ lat: Number(p.property_address_latitude), lng: Number(p.property_address_longitude) }));
  if (coords.length === 0) return undefined;

  let north = Math.max(...coords.map((c) => c.lat));
  let south = Math.min(...coords.map((c) => c.lat));
  let east = Math.max(...coords.map((c) => c.lng));
  let west = Math.min(...coords.map((c) => c.lng));

  const latSpan = north - south;
  const lngSpan = east - west;
  if (latSpan < MIN_BOUNDS_SPAN) {
    const pad = (MIN_BOUNDS_SPAN - latSpan) / 2;
    north += pad;
    south -= pad;
  }
  if (lngSpan < MIN_BOUNDS_SPAN) {
    const pad = (MIN_BOUNDS_SPAN - lngSpan) / 2;
    east += pad;
    west -= pad;
  }

  return { north, south, east, west };
}

export function MarketPage({ title, initialBounds, properties: scopedProperties, children }: MarketPageProps) {
  // Use scoped property set when provided (market-grouped), otherwise full set
  const allSummaries = (scopedProperties && scopedProperties.length > 0 ? scopedProperties : propertySummaries) as any[];

  // Read URL search params on mount to initialize filters (e.g. from Popular Searches links)
  const initialFilters = useMemo<FilterState>(() => {
    const defaults: FilterState = {
      minPrice: 'any',
      maxPrice: 'any',
      beds: 'any',
      baths: 'any',
      sortBy: 'price',
      sortOrder: 'asc',
      moveinDate: 'any',
    };
    if (typeof window === 'undefined') return defaults;
    const params = new URLSearchParams(window.location.search);
    if (params.get('beds')) defaults.beds = params.get('beds')!;
    if (params.get('baths')) defaults.baths = params.get('baths')!;
    if (params.get('min-price')) defaults.minPrice = params.get('min-price')!;
    if (params.get('max-price')) defaults.maxPrice = params.get('max-price')!;
    if (params.get('sort')) defaults.sortBy = params.get('sort')!;
    if (params.get('order')) defaults.sortOrder = params.get('order')!;
    if (params.get('movein')) defaults.moveinDate = params.get('movein')!;
    return defaults;
  }, []);

  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [mobileTab, setMobileTab] = useState<'list' | 'map'>('list');
  const [highlightMapId, setHighlightMapId] = useState<number | null>(null);
  const [fullProperties, setFullProperties] = useState<any[] | null>(null);

  // Map viewport bounds — sidebar shows only properties within these bounds
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(initialBounds || null);

  // Load full property data (with collections) for richer filtering/display
  useEffect(() => {
    if (!allSummaries.length) return;

    const allCached = allSummaries.every((p: any) => !p._slug || detailCache.has(p._slug));
    if (allCached) {
      setFullProperties(allSummaries.map((p: any) => detailCache.get(p._slug) || p));
      return;
    }

    const toLoad = allSummaries.filter((p: any) => p._slug && !detailCache.has(p._slug));
    Promise.all(
      toLoad.map((p: any) =>
        fetchPropertyDetail(p._slug, p.multihub_property_id)
          .then((result) => {
            if (result) {
              detailCache.set(p._slug, {
                ...result,
                _slug: p._slug,
                _stateSlug: p._stateSlug,
                _citySlug: p._citySlug,
              });
            }
          })
          .catch(() => {})
      )
    ).then(() => {
      setFullProperties(allSummaries.map((p: any) => detailCache.get(p._slug) || p));
    });
  }, [allSummaries]);

  const allProperties = fullProperties || allSummaries;

  // ── Price / Beds / Baths / Sort filtering ──────────────────────────────

  const filteredProperties = useMemo(() => {
    let props = [...allProperties];

    if (filters.minPrice !== 'any') {
      const min = Number(filters.minPrice);
      props = props.filter((p: any) => {
        const price = getStartingPrice(p);
        return price != null && price >= min;
      });
    }

    if (filters.maxPrice !== 'any') {
      const max = Number(filters.maxPrice);
      props = props.filter((p: any) => {
        const price = getStartingPrice(p);
        return price == null || price <= max;
      });
    }

    if (filters.beds !== 'any') {
      const bed = Number(filters.beds);
      props = props.filter((p: any) => {
        const min = Number(p.property_bedrooms_min || 0);
        const max = Number(p.property_bedrooms_max || min);
        if (min > 0 && bed >= min && bed <= max) return true;
        if (p.floorplans && Array.isArray(p.floorplans)) {
          return p.floorplans.some((fp: any) => {
            const fmin = Number(fp.floorplan_beds_min || 0);
            const fmax = Number(fp.floorplan_beds_max || fmin);
            return bed >= fmin && bed <= fmax;
          });
        }
        return false;
      });
    }

    if (filters.baths !== 'any') {
      const bath = Number(filters.baths);
      props = props.filter((p: any) => {
        const min = Number(p.property_bathrooms_min || 0);
        const max = Number(p.property_bathrooms_max || min);
        if (min > 0 && bath >= min && bath <= max) return true;
        if (p.floorplans && Array.isArray(p.floorplans)) {
          return p.floorplans.some((fp: any) => Number(fp.floorplan_baths_min || 0) <= bath);
        }
        return false;
      });
    }

    props.sort((a: any, b: any) => {
      if (filters.sortBy === 'price') {
        const pa = getStartingPrice(a) || 0;
        const pb = getStartingPrice(b) || 0;
        return filters.sortOrder === 'asc' ? pa - pb : pb - pa;
      }
      const stripThe = (s: string) => (s.toLowerCase().startsWith('the ') ? s.substring(4) : s);
      const na = stripThe(a.property_name || '');
      const nb = stripThe(b.property_name || '');
      return filters.sortOrder === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
    });

    return props;
  }, [allProperties, filters]);

  // ── Viewport-filtered properties (sidebar list) ────────────────────────

  const visibleProperties = useMemo(() => {
    if (!mapBounds) return filteredProperties;
    return filteredProperties.filter((p: any) => {
      const lat = Number(p.property_address_latitude);
      const lng = Number(p.property_address_longitude);
      if (!lat || !lng) return false;
      return lat >= mapBounds.south && lat <= mapBounds.north && lng >= mapBounds.west && lng <= mapBounds.east;
    });
  }, [filteredProperties, mapBounds]);

  const handleFilterChange = useCallback((partial: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  const handleBoundsChanged = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds);
  }, []);

  // ── Map data (ALL filtered properties get markers) ─────────────────────

  // Use property name as a stable identifier for map pin matching between
  // the displayed card list and the map markers.
  const mapProperties: MapProperty[] = useMemo(() => {
    return filteredProperties
      .filter((p: any) => p.property_address_latitude && p.property_address_longitude)
      .map((p: any, i: number) => ({
        id: i,
        lat: Number(p.property_address_latitude),
        lng: Number(p.property_address_longitude),
        name: p.property_name || '',
        address: `${p.property_address_1 || p.property_address || ''}<br>${p.property_address_city || ''}, ${p.property_address_state || ''} ${p.property_address_zip || ''}`,
        phone: p.property_phone || '',
        description: '',
        url: getPropertyUrl(p),
        directionsUrl: getPropertyAddressUrl(p) || '',
      }));
  }, [filteredProperties]);

  // ── Popular searches data ──────────────────────────────────────────────

  const { allBeds, allAmenities } = useMemo(() => {
    const beds = new Set<number>();
    const amenities = new Set<string>();
    allProperties.forEach((p: any) => {
      if (p.floorplans && Array.isArray(p.floorplans)) {
        p.floorplans.forEach((fp: any) => {
          const min = Number(fp.floorplan_beds_min);
          const max = Number(fp.floorplan_beds_max || min);
          for (let i = min; i <= max; i++) {
            if (i > 0) beds.add(i);
          }
        });
      }
      const bMin = Number(p.property_bedrooms_min);
      const bMax = Number(p.property_bedrooms_max || bMin);
      if (bMin > 0) for (let i = bMin; i <= bMax; i++) beds.add(i);

      if (p.amenities && Array.isArray(p.amenities)) {
        p.amenities.forEach((a: any) => {
          const t = typeof a === 'string' ? a : a?.title || a?.name || a?.label || '';
          if (t.trim()) amenities.add(t.trim());
        });
      }
      const amenityCandidates = [
        p.property_amenity_highlights,
        p.property_amenities,
        p.property_community_amenities_list,
        p.property_apartment_features_list,
        p.community_amenities,
        p.features,
        p.highlights,
      ];
      for (const a of amenityCandidates) {
        if (!a) continue;
        const items = Array.isArray(a) ? a : typeof a === 'string' ? a.split(',') : [];
        items.forEach((v: any) => {
          const val = typeof v === 'string' ? v : v?.title || v?.name || '';
          if (val.trim()) amenities.add(val.trim());
        });
      }
    });
    return { allBeds: [...beds], allAmenities: [...amenities] };
  }, [allProperties]);

  // ── Render ─────────────────────────────────────────────────────────────

  if (!allSummaries.length) {
    return (
      <section className="case-market" style={{ padding: '4rem 1.5rem', textAlign: 'center', color: '#999' }}>
        <p>No apartments found.</p>
      </section>
    );
  }

  return (
    <div className="case-market">
      <CityFilterBar
        title={title}
        propertyCount={visibleProperties.length}
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      <div className="case-market-body">
        <div className={`case-market-list-col${mobileTab === 'map' ? ' case-mobile-hidden' : ''}`}>
          <div className="case-market-list-inner">
            {visibleProperties.length === 0 && (
              <div style={{ padding: '2rem 1.5rem', textAlign: 'center', color: '#999' }}>
                <p>No apartments in this area. Try zooming out or adjusting filters.</p>
              </div>
            )}
            {visibleProperties.map((prop: any, i: number) => (
              <CityPropertyCard
                key={prop.property_name + i}
                name={prop.property_name || ''}
                address={prop.property_address_1 || prop.property_address || ''}
                city={prop.property_address_city || ''}
                state={prop.property_address_state || ''}
                zip={prop.property_address_zip || ''}
                phone={prop.property_phone || ''}
                url={getPropertyUrl(prop)}
                floorPlansUrl={`${getPropertyUrl(prop)}floor-plans`}
                neighborhoodUrl={`${getPropertyUrl(prop)}neighborhood`}
                amenitySnippet={getAmenitySnippet(prop)}
                startingPrice={getStartingPrice(prop)}
                images={getPropertyImages(prop)}
                onMapPinClick={() => {
                  // Find the matching map marker id by property name, since
                  // visibleProperties (viewport-filtered) indices differ from
                  // mapProperties (all filteredProperties with coords) indices.
                  const mapIdx = mapProperties.findIndex((mp) => mp.name === (prop.property_name || ''));
                  setHighlightMapId(mapIdx >= 0 ? mapIdx : null);
                }}
              />
            ))}
          </div>

          <CityPopularSearches beds={allBeds} amenities={allAmenities} onFilterApply={(partial) => {
            handleFilterChange(partial);
            // Scroll the property list to the top so results are visible
            const listEl = document.querySelector('.case-market-list-inner');
            if (listEl) listEl.scrollIntoView({ behavior: 'smooth' });
          }} />
        </div>

        <div className={`case-market-map-col${mobileTab === 'list' ? ' case-mobile-hidden' : ''}`}>
          <div className="case-map-holder-wrap">
            <CityGoogleMap
              properties={mapProperties}
              apiKey={GOOGLE_MAPS_API_KEY}
              highlightId={highlightMapId}
              initialBounds={initialBounds}
              onBoundsChanged={handleBoundsChanged}
            />
          </div>
        </div>
      </div>

      <CityMobileTabs activeTab={mobileTab} onTabChange={setMobileTab} />

      {children}
    </div>
  );
}

// ── Helper functions ───────────────────────────────────────────────────────

export function getPropertyUrl(prop: any): string {
  return `/apartments/${prop._stateSlug}/${prop._citySlug}/${prop._slug || prop.property_slug}/`;
}

export function getAmenitySnippet(prop: any): string {
  if (prop.amenities && Array.isArray(prop.amenities) && prop.amenities.length > 0) {
    const titles = prop.amenities
      .map((a: any) => a.title || a.name || a.label || (typeof a === 'string' ? a : ''))
      .filter(Boolean);
    if (titles.length > 0) return titles.slice(-2).join(', ');
  }
  const listCandidates = [
    prop.property_community_amenities_list,
    prop.property_apartment_features_list,
    prop.property_amenity_highlights,
    prop.property_amenities,
    prop.community_amenities,
    prop.features,
    prop.highlights,
  ];
  for (const list of listCandidates) {
    if (Array.isArray(list) && list.length > 0) {
      const items = list.map((a: any) => (typeof a === 'string' ? a : a?.title || a?.name || '')).filter(Boolean);
      if (items.length > 0) return items.slice(-2).join(', ');
    }
    if (typeof list === 'string' && list) {
      const items = list
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);
      if (items.length > 0) return items.slice(-2).join(', ');
    }
  }
  return '';
}

// Minimum credible monthly rent — anything below this is likely a deposit,
// pet rent, storage fee, or bad data and should be ignored.
const MIN_CREDIBLE_RENT = 200;

function parsePrice(raw: any): number | null {
  if (!raw || raw === true || raw === 'false') return null;
  const n = Number(String(raw).replace(/[^0-9.]/g, ''));
  return (!isNaN(n) && n >= MIN_CREDIBLE_RENT) ? n : null;
}

export function getStartingPrice(prop: any): number | null {
  // 1. Top-level property_price_min (Yardi feed) — trusted when ≥ $200
  const topLevel = parsePrice(prop.property_price_min);
  if (topLevel != null) return topLevel;

  // 2. Lowest floorplan price
  if (prop.floorplans && Array.isArray(prop.floorplans) && prop.floorplans.length > 0) {
    const prices = prop.floorplans
      .map((fp: any) => {
        for (const v of [fp.floorplan_price_min, fp.price_min, fp.rent_min, fp.min_price]) {
          const n = parsePrice(v);
          if (n != null) return n;
        }
        return NaN;
      })
      .filter((n: number) => !isNaN(n));
    if (prices.length > 0) return Math.min(...prices);
  }

  // 3. No credible price found — caller renders plain CTA
  return null;
}

const IMAGE_LIMIT = 6;

export function getPropertyImages(prop: any): string[] {
  let imgs: string[] = [];

  if (prop.photo_gallery && Array.isArray(prop.photo_gallery)) {
    const withImages = prop.photo_gallery.filter((g: any) => g.image);
    const community = withImages.filter((g: any) => (g.title || '').toLowerCase() === 'community');
    const apartment = withImages.filter((g: any) => (g.title || '').toLowerCase() === 'apartment');
    const other = withImages.filter((g: any) => {
      const t = (g.title || '').toLowerCase();
      return t !== 'community' && t !== 'apartment';
    });
    imgs = [...community, ...apartment, ...other].map((g: any) => g.image);
  } else if (prop.property_images && Array.isArray(prop.property_images)) {
    imgs = prop.property_images;
  }

  if (prop.property_featured_image) {
    imgs = [prop.property_featured_image, ...imgs.filter((u: string) => u !== prop.property_featured_image)];
  }

  return imgs.length > 0 ? imgs.slice(0, IMAGE_LIMIT) : [];
}

export default MarketPage;
